<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="500"
    persistent
  >
    <v-card v-if="receipt">
      <!-- Cabeçalho -->
      <v-card-title class="primary white--text py-3">
        <v-icon left dark>mdi-receipt</v-icon>
        Comprovante de Registro de Ponto
      </v-card-title>

      <v-card-text class="pt-4">
        <!-- NSR em destaque -->
        <v-sheet
          color="primary"
          dark
          rounded
          class="pa-3 mb-4 text-center"
        >
          <div class="caption">NSR - Número Sequencial do Registro</div>
          <div class="text-h4 font-weight-bold">{{ receipt.nsr || 'N/A' }}</div>
        </v-sheet>

        <!-- Data/Hora em destaque -->
        <v-sheet
          color="blue lighten-5"
          rounded
          class="pa-3 mb-4 text-center"
        >
          <div class="text-h6 primary--text">{{ receipt.datetime?.date || 'N/A' }}</div>
          <div class="text-h4 font-weight-bold">{{ receipt.datetime?.time || 'N/A' }}</div>
          <div class="caption grey--text">Fuso horário: {{ receipt.timezone || 'America/Sao_Paulo' }}</div>
        </v-sheet>

        <!-- Dados do Trabalhador -->
        <v-list-item two-line class="px-0">
          <v-list-item-icon>
            <v-icon color="primary">mdi-account</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-subtitle>Trabalhador</v-list-item-subtitle>
            <v-list-item-title class="font-weight-medium">
              {{ receipt.worker?.name || 'N/A' }}
            </v-list-item-title>
            <v-list-item-subtitle>
              CPF: {{ receipt.worker?.cpf_masked || 'N/A' }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <v-divider class="my-2"></v-divider>

        <!-- Dados do Empregador -->
        <v-list-item two-line class="px-0">
          <v-list-item-icon>
            <v-icon color="primary">mdi-domain</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-subtitle>Empregador</v-list-item-subtitle>
            <v-list-item-title class="font-weight-medium">
              {{ receipt.employer?.name || 'N/A' }}
            </v-list-item-title>
            <v-list-item-subtitle>
              CNPJ: {{ receipt.employer?.cnpj || 'N/A' }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <v-divider class="my-2"></v-divider>

        <!-- Local -->
        <v-list-item two-line class="px-0">
          <v-list-item-icon>
            <v-icon color="primary">mdi-map-marker</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-subtitle>Local</v-list-item-subtitle>
            <v-list-item-title class="font-weight-medium text-wrap">
              {{ receipt.location || 'N/A' }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-divider class="my-2"></v-divider>

        <!-- Coletor -->
        <v-list-item two-line class="px-0">
          <v-list-item-icon>
            <v-icon color="primary">mdi-cellphone-link</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-subtitle>Coletor / Dispositivo</v-list-item-subtitle>
            <v-list-item-title class="font-weight-medium">
              {{ receipt.collector?.ip || 'N/A' }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-divider class="my-2"></v-divider>

        <!-- Hash SHA-256 -->
        <v-sheet
          color="orange lighten-5"
          rounded
          class="pa-3 mt-4"
        >
          <div class="caption orange--text text--darken-2 font-weight-medium mb-1">
            <v-icon small color="orange darken-2" class="mr-1">mdi-shield-check</v-icon>
            Assinatura Digital (SHA-256)
          </div>
          <code class="d-block text-caption" style="word-break: break-all;">
            {{ receipt.hash || 'N/A' }}
          </code>
        </v-sheet>

        <!-- Aviso Legal -->
        <v-alert
          type="info"
          dense
          text
          class="mt-4 mb-0 caption"
        >
          Este comprovante atende aos requisitos da Portaria MTP nº 671/2021.
          A assinatura digital garante a integridade deste registro.
        </v-alert>
      </v-card-text>

      <v-divider></v-divider>

      <v-alert v-if="downloadError" type="error" dense outlined class="mx-4 mt-3 mb-0">
        {{ downloadError }}
      </v-alert>

      <v-card-actions class="pa-4">
        <v-btn
          v-if="receipt.pdf_url"
          color="primary"
          outlined
          :loading="downloading"
          :disabled="downloading"
          @click="downloadPdf"
        >
          <v-icon left>mdi-download</v-icon>
          Baixar PDF
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="close"
        >
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'TimePunchReceipt',

  data: () => ({
    downloading: false,
    downloadError: ''
  }),

  props: {
    value: {
      type: Boolean,
      default: false
    },
    receipt: {
      type: Object,
      default: () => null
    }
  },

  computed: {
    dialogVisible: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    }
  },

  methods: {
    close() {
      this.dialogVisible = false
      this.$emit('close')
    },

    // A rota do PDF exige o token JWT, que um window.open direto não envia (401).
    // Baixa o arquivo com a requisição autenticada e abre a partir de um blob.
    downloadPdf() {
      if (!this.receipt?.pdf_url || this.downloading) {
        return
      }

      this.downloading = true
      this.downloadError = ''

      this.$http.get(this.receipt.pdf_url, { responseType: 'blob' })
        .then((res) => {
          const blob = new Blob([res.body], { type: 'application/pdf' })
          const fileUrl = (window.URL || window.webkitURL).createObjectURL(blob)
          window.open(fileUrl, '_blank')
        })
        .catch(() => {
          this.downloadError = 'Não foi possível baixar o comprovante em PDF. Tente novamente.'
        })
        .finally(() => {
          this.downloading = false
        })
    }
  }
}
</script>

<style scoped>
.text-wrap {
  white-space: normal;
  word-break: break-word;
}
</style>
